import React, { useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography, Box } from "@mui/material";

// Placeholder for external helpers
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Checks if a column header represents a numeric value that should be right-aligned
 * and formatted (e.g., sales amounts, returns, VAT, percentages).
 */
const isNumericHeader = (header) => {
    // Explicitly exclude descriptive keys from being formatted as numbers
    if (header.toUpperCase().includes('CODE') || header.toUpperCase().includes('NAME')) {
        return false;
    }
    
    // Check if it contains common numeric keywords (e.g., SALES, RETURN, VAT, AMT, %)
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

// Define the two fixed, sticky descriptive columns
const PRIMARY_STICKY_KEY = 'GRPCODE';
const SECONDARY_STICKY_KEY = 'GROUPNAME';
const STICKY_COLUMNS = [PRIMARY_STICKY_KEY, SECONDARY_STICKY_KEY];
const PRIMARY_STICKY_WIDTH = 150;
const SECONDARY_STICKY_WIDTH = 300; // Wider column for the group name

// Define the specific numeric columns the user wants to total in the footer
const FOOTER_TOTAL_KEYS = [
    'CASH SALES', 
    'NETCASH SALES', 
    'NETCASH SALESRETURN',
    'CREDIT SALES', 
    'NETCREDIT SALES', 
    'NETCREDIT SALESRETURN', 
    'SALES RETURN', 
    'NETSALES RETURN', 
    'VAT AMT', 
    'COMMISSION', 
    'NET SALES',
    'NET SALES EXCLVAT'
];


const SalesTableCustomerGroupwiseAggregated = ({ rows, loading, loadNextPage, hasMore }) => { 

    const tableContainerRef = useRef(null);
    const handleTableScroll = () => {
        const currentTarget = tableContainerRef.current;
        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };

    // 1. Determine the non-sticky (remaining) display columns
    const remainingDisplayKeys = useMemo(() => {
        if (rows.length === 0) return [];
        
        const allKeys = Object.keys(rows[0]);
        
        // Filter out the sticky keys and internal flags
        return allKeys.filter(key =>
            !STICKY_COLUMNS.includes(key) &&
            !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', 'YEAR'].includes(key)
        );
    }, [rows]);


    // 2. Calculate the Grand Totals internally ONLY for the FOOTER_TOTAL_KEYS
    const internalGrandTotals = useMemo(() => {
        if (rows.length === 0) return {};

        // Identify keys for totaling: must be explicitly requested in FOOTER_TOTAL_KEYS
        const keysToTotal = remainingDisplayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        
        const totals = {};

        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }

        return totals;
    }, [rows, remainingDisplayKeys]);
    
    // Keys for the columns that will display a total in the footer, preserving display order
    const totalValueKeys = remainingDisplayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));

    // Determine total columns for colspan calculation
    // Total columns = 2 (Sticky Columns) + remainingDisplayKeys.length
    const totalColumns = STICKY_COLUMNS.length + remainingDisplayKeys.length;
    
    // --- Logic to calculate descriptiveSpan for the footer label ---
    let descriptiveSpan = totalColumns; // Default to full span if no totals
    
    if (totalValueKeys.length > 0) {
        // Find the index of the first column that will have a total in the remainingDisplayKeys array.
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndexInRemaining = remainingDisplayKeys.indexOf(firstTotalKey);
        
        // The span covers: 2 (Sticky Columns) + firstTotalIndexInRemaining
        if (firstTotalIndexInRemaining !== -1) {
            descriptiveSpan = STICKY_COLUMNS.length + firstTotalIndexInRemaining;
        }
    }
    // --------------------

    // Conditional early return moved below hook calls
    if (rows.length === 0 && !loading) return null;


    const renderHeader = () => {
        let leftOffset = 0; // Tracks the cumulative width for sticky columns

        return (
            <TableHead>
                <TableRow>
                    {/* 1. Render Sticky Columns (GRPCODE and GROUPNAME) */}
                    {STICKY_COLUMNS.map((columnKey, index) => {
                        const minWidth = columnKey === SECONDARY_STICKY_KEY ? SECONDARY_STICKY_WIDTH : PRIMARY_STICKY_WIDTH; 
                        
                        const stickyStyle = {
                            position: 'sticky',
                            left: leftOffset,
                            zIndex: 10,
                            backgroundColor: '#e0f7fa',
                            borderRight: '1px solid #ccc',
                            minWidth: minWidth,
                            whiteSpace: 'nowrap',
                        };

                        leftOffset += minWidth; 

                        return (
                            <TableCell 
                                key={columnKey} 
                                style={stickyStyle}
                            >
                                {columnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                            </TableCell>
                        );
                    })}

                    {/* 2. Dynamic Headers for the rest of the columns */}
                    {remainingDisplayKeys.map(key => (
                        <TableCell
                            key={key}
                            align={isNumericHeader(key) ? "right" : "left"}
                            style={{ minWidth: 120, whiteSpace: 'nowrap' }}
                        >
                            {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    };

    const renderBody = () => {
        return (
            <TableBody>
                {rows.map((row, index) => {
                    
                    let leftOffset = 0; // Reset offset for each row/row group

                    return (
                        <TableRow key={`agg-group-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            
                            {/* 1. Render Sticky Cells (GRPCODE and GROUPNAME) */}
                            {STICKY_COLUMNS.map((columnKey, colIndex) => {
                                const minWidth = columnKey === SECONDARY_STICKY_KEY ? SECONDARY_STICKY_WIDTH : PRIMARY_STICKY_WIDTH; 

                                const stickyStyle = {
                                    position: 'sticky', 
                                    left: leftOffset, 
                                    zIndex: 9, 
                                    backgroundColor: '#ffffff', 
                                    borderRight: '1px solid #ccc',
                                };

                                leftOffset += minWidth; 

                                return (
                                    <TableCell 
                                        key={columnKey}
                                        style={stickyStyle}
                                    >
                                        {/* Handle null values gracefully */}
                                        {row[columnKey] === null || row[columnKey] === undefined ? 'N/A' : row[columnKey]}
                                    </TableCell>
                                );
                            })}
                            
                            {/* 2. Data Cells (Remaining Columns) */}
                            {remainingDisplayKeys.map(key => (
                                <TableCell key={key} align={isNumericHeader(key) ? "right" : "left"}>
                                    {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    );
                })}

                
                {/* Load More Indicator - Placed correctly inside TableBody */}
                {hasMore && (
                    <TableRow>
                        <TableCell colSpan={totalColumns} align="center">
                            <Box sx={{ py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="caption" display="block">
                                    Loading more data...
                                </Typography>
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                

                {!loading && rows.length === 0 && (<TableRow><TableCell colSpan={totalColumns} align="center"><Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography></TableCell></TableRow>)}
            </TableBody>
        );
    };


    const renderFooter = () => {
        // Find the index in remainingDisplayKeys where the totals start
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndexInRemaining = remainingDisplayKeys.indexOf(firstTotalKey);

        return (
            <TableFooter>
                <TableRow sx={{ backgroundColor: '#80cbc4', borderTop: '3px double #000' }}>
                    {/* 1. Descriptive Span for the GRAND TOTAL label */}
                    <TableCell colSpan={descriptiveSpan} align="right"
                        sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#80cbc4', borderRight: '3px double #000' }}>
                        GRAND TOTAL:
                    </TableCell>
                    
                    {/* 2. Data cells starting from the column where the totals begin */}
                    {/* Iterate over the remaining numeric keys starting from where the descriptive span ends */}
                    {remainingDisplayKeys.slice(firstTotalIndexInRemaining).map(key => {
                        const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                        return (
                            <TableCell key={key} align="right"
                                sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#80cbc4' }}>
                                {/* Show total if key is in FOOTER_TOTAL_KEYS, otherwise show blank */}
                                {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                            </TableCell>
                        );
                    })}
                </TableRow>
            </TableFooter>
        );
    };

    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll}
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}
        >
            <Table stickyHeader aria-label="aggregated account sales report">

                {renderHeader()}
                {renderBody()}

                {/* Render footer only if totals were calculated */}
                {Object.keys(internalGrandTotals).length > 0 && renderFooter()}
            </Table>

            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}
        </TableContainer>
    );
};

export default SalesTableCustomerGroupwiseAggregated;